import React, { memo, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styles from './styles.module.css';
import {
  Button,
  Input,
  SearchableSelect,
  Toggle,
  Dropdown,
  ConfirmModal,
} from '../../../../../common/ui';
import { getCategories } from '../../../categories/actions';
import { addNewProduct } from '../../actions';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../../../../common/actions';
import ProductVariantsTable from './ProductVariantsTable';
import AddNewVariantPopup from './AddNewVariantPopup';
import ImageUpload from './ImageUpload';
import ModifierGroupAttach from '../ModifierGroupAttach';
import { fetchIsModifierCompany } from '../../../../reports/billHistory/utils/companyType';

const typeOptions = [
  { value: 'STOCK', label: 'Stock' },
  { value: 'NON_STOCK', label: 'Non-Stock' },
];

const AddNewProduct = memo(
  ({
    getCategories,
    categoriesState: { categoriesData },
    addNewProduct: addNewProductAction,
    showAlert,
    auth: { companyId },
  }) => {
    const [isLoading, setLoading] = useState(false);
    const [errors, setErrors] = useState({ name: '', category: '', price: '' });
    const [productForm, setProductForm] = useState({
      code: null,
      name: null,
      categoryid: null,
      barcode: null,
      dateadded: null,
    });
    const [variantAvailability, setVariantAvailability] = useState(false);
    const [productType, setProductType] = useState('STOCK');
    const [isSerialCheck, setIsSerialCheck] = useState(false);
    const [price, setPrice] = useState('');
    const [cost, setCost] = useState('');
    const [availability, setAvailability] = useState(false);
    const [variantsDetails, setVariantsDetails] = useState([]);
    const [showCreateStockDialog, setShowCreateStockDialog] = useState(false);
    const [isAddVariantPopupOpen, setIsAddVariantPopupOpen] = useState(false);
    const [productImages, setProductImages] = useState([]);
    const [modifiersEnabled, setModifiersEnabled] = useState(false);
    const [modifierGroupIds, setModifierGroupIds] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
      getCategories();
    }, [getCategories]);

    useEffect(() => {
      let cancelled = false;
      const load = async () => {
        const enabled = await fetchIsModifierCompany(companyId);
        if (!cancelled) setModifiersEnabled(enabled);
      };
      load();
      return () => {
        cancelled = true;
      };
    }, [companyId]);

    const onFieldChange = (field, value) => {
      setProductForm((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const onCategoryChange = (value) => {
      setProductForm((prev) => ({ ...prev, categoryid: value }));
      if (errors.category) setErrors((prev) => ({ ...prev, category: '' }));
    };

    const validate = () => {
      const nameTrim = (productForm.name ?? '').trim();
      const nameErr = !nameTrim ? 'Product name is required' : '';
      const categoryErr =
        productForm.categoryid == null || productForm.categoryid === ''
          ? 'Please select a category'
          : '';
      const priceErr =
        !variantAvailability &&
          productType === 'NON_STOCK' &&
          (price === '' || price == null)
          ? 'Price is required'
          : '';
      setErrors({ name: nameErr, category: categoryErr, price: priceErr });
      if (variantAvailability && variantsDetails.length === 0) {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Please add at least one variant',
        });
        return false;
      }
      return !nameErr && !categoryErr && !priceErr;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!validate()) return;
      setLoading(true);
      const nameTrim = (productForm.name ?? '').trim();
      const payload = {
        name: nameTrim,
        code: productForm.code ?? '',
        barcode: productForm.barcode ?? '',
        categoryId: productForm.categoryid,
        dateAdded: Date.now(),
        isSerialCheck: isSerialCheck,
        type: productType,
        isVariantAvailable: variantAvailability,
        variantsDetails: variantsDetails,
        images: variantAvailability ? [] : productImages,
      };
      if (modifiersEnabled) {
        payload.modifierGroupIds = modifierGroupIds;
      }
      if (!variantAvailability && productType === 'NON_STOCK') {
        payload.price = price !== '' && price != null ? parseFloat(price) : 0;
        payload.cost = cost !== '' && cost != null ? parseFloat(cost) : 0;
        payload.isAvailable = availability;
      }

      console.log(payload);
      
      const result = await addNewProductAction(payload);
      setLoading(false);
      if (result) {
        if (productType === 'STOCK' && !variantAvailability) {
          setShowCreateStockDialog(true);
        } else {
          showAlert({
            isAlertOpen: true,
            alertType: 'success',
            alertMessage: 'Product created successfully.',
          });
          navigate('/inventory/products');
        }
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Something went wrong. Please try again.',
        });
      }
    };

    const handleCancel = () => navigate('/inventory/products');

    const handleReset = () => {
      setProductForm({
        code: null,
        name: null,
        categoryid: null,
        barcode: null,
        dateadded: null,
      });
      setVariantAvailability(false);
      setProductType('STOCK');
      setIsSerialCheck(false);
      setPrice('');
      setCost('');
      setAvailability(false);
      setVariantsDetails([]);
      setProductImages([]);
      setModifierGroupIds([]);
      setErrors({ name: '', category: '', price: '' });
      setShowCreateStockDialog(false);
    };

    const removeVariantItem = (variant, index) => {
      setVariantsDetails((prev) => prev.filter((_, i) => i !== index));
    };

    const handleAddVariant = (variantData) => {
      setVariantsDetails((prev) => [...prev, variantData]);
    };

    const handleCreateStockYes = () => {
      setShowCreateStockDialog(false);
      showAlert({
        isAlertOpen: true,
        alertType: 'success',
        alertMessage: 'New Product created successfully.',
      });
      handleReset();
      navigate('/inventory/products');
    };

    const handleCreateStockNo = () => {
      setShowCreateStockDialog(false);
      showAlert({
        isAlertOpen: true,
        alertType: 'success',
        alertMessage: 'New Product created successfully.',
      });
      handleReset();
      navigate('/inventory/products');
    };

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>New Product</h1>
            <p className={styles.breadcrumb}>Inventory / Products / New</p>
          </div>
        </div>
        <div className={styles.body}>
          <form className={styles.formCard} onSubmit={handleSubmit} noValidate>
            <div className={styles.formFields}>
              <div className={styles.formRow}>
                <div className={styles.fieldGroup}>
                  <Input
                    id="name"
                    label="Name"
                    type="text"
                    value={productForm.name ?? ''}
                    onChange={(value) => onFieldChange('name', value)}
                    error={errors.name}
                    placeholder="Enter product name"
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <Input
                    id="code"
                    label="Code"
                    type="text"
                    value={productForm.code ?? ''}
                    onChange={(value) => onFieldChange('code', value)}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.fieldGroup}>
                  <Input
                    id="barcode"
                    label="Barcode"
                    type="text"
                    value={productForm.barcode ?? ''}
                    onChange={(value) => onFieldChange('barcode', value)}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <SearchableSelect
                    label="Category"
                    options={categoriesData ?? []}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    value={productForm.categoryid}
                    onChange={onCategoryChange}
                    placeholder="Not selected"
                  />
                  {errors.category ? (
                    <span className={styles.fieldError}>{errors.category}</span>
                  ) : null}
                </div>
              </div>

              <div className={styles.divider} />

              <div className={styles.formRow}>
                <div className={styles.fieldGroup}>
                  <Toggle
                    label="Variants Available"
                    labelPlacement="start"
                    name="variantAvailability"
                    checked={variantAvailability}
                    onChange={(e) => {
                      setVariantAvailability(e.target.checked);
                      setProductImages([]);
                    }}
                  />
                </div>
                <div className={styles.fieldGroup} />
              </div>

              <div className={styles.formRow}>
                <div className={styles.fieldGroup}>
                  <Dropdown
                    label="Type"
                    value={productType}
                    onChange={(value) => setProductType(value)}
                    options={typeOptions}
                    placeholder="Select type"
                  />
                </div>
                <div className={styles.fieldGroup} />
              </div>

              {variantAvailability && (
                <>
                  <div className={styles.addVariantRow}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setIsAddVariantPopupOpen(true)}
                    >
                      Add Variant
                    </Button>
                  </div>
                  <ProductVariantsTable
                    rows={variantsDetails}
                    removeVariantItem={removeVariantItem}
                    productType={productType}
                  />
                </>
              )}

              {!variantAvailability && productType === 'NON_STOCK' && (
                <>
                  <div className={styles.formRow}>
                    <div className={styles.fieldGroup}>
                      <Input
                        label="Price"
                        type="text"
                        value={price}
                        onChange={(value) => {
                          setPrice(value);
                          if (errors.price) setErrors((prev) => ({ ...prev, price: '' }));
                        }}
                        placeholder="0.00"
                        error={errors.price}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <Input
                        label="Cost"
                        type="text"
                        value={cost}
                        onChange={(value) => setCost(value)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.fieldGroup}>
                      <Toggle
                        label="Availability"
                        labelPlacement="start"
                        name="availability"
                        checked={availability}
                        onChange={(e) =>
                          setAvailability(e.target.checked)
                        }
                      />
                    </div>
                    <div className={styles.fieldGroup} />
                  </div>
                </>
              )}

              {!variantAvailability && productType === 'STOCK' && (
                <div className={styles.formRow}>
                  <div className={styles.fieldGroup}>
                    <Toggle
                      label="Serial Number Validation"
                      labelPlacement="start"
                      name="isSerialCheck"
                      checked={isSerialCheck}
                      onChange={(e) =>
                        setIsSerialCheck(e.target.checked)
                      }
                    />
                  </div>
                  <div className={styles.fieldGroup} />
                </div>
              )}
            </div>

            {!variantAvailability && (
              <>
                <div className={styles.divider} />
                <ImageUpload
                  images={productImages}
                  onChange={setProductImages}
                />
              </>
            )}

            {modifiersEnabled && (
              <>
                <div className={styles.divider} />
                <div className={styles.formRow}>
                  <div className={styles.fieldGroup}>
                    <h3>Modifier groups</h3>
                    <ModifierGroupAttach
                      companyId={companyId}
                      selectedIds={modifierGroupIds}
                      onChange={setModifierGroupIds}
                    />
                  </div>
                </div>
              </>
            )}

            <div className={styles.buttonPanel}>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving…' : 'Create product'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleReset}
                disabled={isLoading}
              >
                Reset
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </form>

          <ConfirmModal
            open={showCreateStockDialog}
            onClose={handleCreateStockNo}
            onConfirm={handleCreateStockYes}
            title="Create Stock"
            message="Do you need to create stock for this product?"
            confirmLabel="Yes"
            cancelLabel="No"
          />

          <AddNewVariantPopup
            isOpen={isAddVariantPopupOpen}
            onClose={() => setIsAddVariantPopupOpen(false)}
            productType={productType}
            onAddVariant={handleAddVariant}
          />
        </div>
      </div>
    );
  }
);

const mapStateToProps = (state) => ({
  productsState: state.productsState,
  auth: state.authState,
  categoriesState: state.categoriesState,
});

export default connect(mapStateToProps, {
  getCategories,
  addNewProduct,
  showAlert,
})(AddNewProduct);
